<template>
  <section class="bg-white rounded mb-4 p-3 p-sm-4">
    <h2 class="mb-sm-3">Rookie Horse Nominations</h2>
    <div class="row align-items-start">
      <div class="col-12 col-md-auto form-group">
        <label for="year">Year</label>
        <select id="year" class="form-control pr-5" v-model="selectedYear" @change="$nextTick(() => {getRookies()})">
          <option v-for="(year, yearIndex) in years" :key="yearIndex" :value="year.id">{{year.name}}</option>
        </select>
        <small>Please save any nomination changes before changing the year.</small>
      </div>
    </div>

    <div class="form-row align-items-start" v-for="(rookie, rookieIndex) in selectedHorses" :key="rookieIndex">
      <div class="col-12 col-md form-group">
        <label :for="'horse' + rookieIndex">Horse</label>
        <div :id="'horsediv' + rookieIndex" :class="[{ 'click-events-none not-required' : rookie.id }]">
          <horse-search-select
            :element-id="'horse' + rookieIndex"
            :value="rookieHorseValue(rookie)"
            :disabled="!!rookie.id"
            @input="v => $set(selectedHorses[rookieIndex].horse, 'id', v)"
          />
        </div>
      </div>
      <div class="col-12 col-md form-group">
        <label :for="'horseDate' + rookieIndex">Eligible From</label>
        <input type="date" :id="'horseDate' + rookieIndex" class="form-control" v-model="selectedHorses[rookieIndex].eligible_from" :disabled="!!rookie.id">
      </div>
      <div class="col-12 col-md-auto form-group" v-if="!rookie.id">
        <label aria-hidden="true" class="d-none d-md-block">&nbsp;</label>
        <button class="btn btn-block-mob btn-outline-primary" @click="saveRookie(rookieIndex)">Save</button>
      </div>
      <div :class="['col-12 col-md form-group pb-lg-1 pl-md-0', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
        <label aria-hidden="true" class="d-none d-md-block">&nbsp;</label>
        <button @click.prevent="removeResult(rookieIndex)" aria-label="Remove Result" class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1 mr-n-2 d-none d-md-block">
          <div class="btn-remove">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17"><g fill="currentColor"><path d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/><path d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/><path d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/></g></svg>
          </div>
        </button>
        <button class="btn btn-block-mob btn-outline-primary d-md-none" @click="removeResult(rookieIndex)" aria-label="Remove Result">Remove</button>
      </div>
    </div>
    <div class="col-12 col-md form-group align-self-end" v-if="selectedHorses.length === 0">
      <div class="text-center py-2 m-auto">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>

    <div class="d-lg-flex justify-content-end mt-3">
      <button class="btn btn-block-mob btn-primary" @click.prevent="addAnotherResult();focusNew()">Add Another Rookie</button>
    </div>
    <div role="alert" class="alert alert-success mt-3 mb-0" v-if="rookieSuccess">{{rookieSuccess}}</div>
    <div role="alert" class="alert alert-warning mt-3 mb-0" v-if="rookieError">{{rookieError}}</div>
  </section>
</template>

<script>
import axios from 'axios'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import HorseSearchSelect from '@/components/Horses/HorseSearchSelect'

export default {
  components: {
    'horse-search-select': HorseSearchSelect
  },
  mixins: [LEGACYBROWSERS],
  data() {
    return {
      selectedHorses: [],
      selectedYear: 0,
      years: [],
      processing: false,
      rookieSuccess: '',
      rookieError: ''
    }
  },
  methods: {
    // rookie.horse.id may be undefined (new), a string (loaded), or ['id'] (just picked).
    rookieHorseValue(rookie) {
      const id = rookie && rookie.horse ? rookie.horse.id : null
      if (!id) return []
      return Array.isArray(id) ? id : [id.toString()]
    },
    saveRookie(horseIndex) {
      this.processing = true
      this.rookieSuccess = ''
      this.rookieError = ''
      const data = JSON.parse(JSON.stringify(this.selectedHorses[horseIndex]))
      data.year = this.selectedYear
      if (data.horse.id && data.horse.id.length) {
        data.horse.id = Number(data.horse.id)
      } else {
        data.horse.id = null
      }
      axios.post(`${this.$api}/admin/rookie-nominations`, data).then((response) => {
        this.processing = false
        this.rookieSuccess = `Rookie List for ${this.selectedYear} saved successfully.`
        let newData = JSON.parse(JSON.stringify(this.selectedHorses))
        const returnedData = response.data
        // ID is being sent to front-end as a Number - needs to be string for comparison
        returnedData.horse.id = returnedData.horse.id.toString()
        // Return new data with id from back-end instead of entered data on front-end
        newData = newData.map(item => (item.horse.id[0] === returnedData.horse.id ? returnedData : item))
        this.selectedHorses = newData
      }).catch(() => {
        this.processing = false
        this.rookieError = `Please check the data in your submission.`
      })
    },
    getRookies() {
      this.selectedHorses = []
      this.rookieSuccess = ''
      this.rookieError = ''
      axios.get(`${this.$api}/admin/rookie-nominations`, { params: { year: this.selectedYear === 0 ? null : this.selectedYear } }).then((response) => {
        this.selectedHorses = response.data.map((horse) => {
          const tempHorse = horse
          if (tempHorse.horse && tempHorse.horse.id) {
            tempHorse.horse.id = tempHorse.horse.id.toString()
          }
          return tempHorse
        })
        this.addAnotherResult()
      }).catch(() => {
        this.rookieError = `Rookie list retrieval failed. Please try again later.`
      })
    },
    focusNew() {
      setTimeout(() => {
        // HorseSearchSelect only renders its <input> while active, so a freshly added
        // row has no <input> yet - focus its always-present .multiselect root instead,
        // which triggers HorseSearchSelect's own focusin handling to activate + focus it.
        const target = document.body.querySelector(`#horsediv${this.selectedHorses.length - 1} .multiselect`)
        if (target) target.focus()
      }, 10)
    },
    removeResult(index) {
      this.rookieSuccess = ''
      this.rookieError = ''
      if (this.selectedHorses[index].id) {
        axios.delete(`${this.$api}/admin/rookie-nominations/${this.selectedHorses[index].id}`).then(() => {
          // Not sure why this doesn't need this.$forceUpdate()
          this.selectedHorses.splice(index, 1)
        }).catch(() => {
          this.rookieError = `Could not remove record.`
        })
      } else {
        // Not sure why this doesn't need this.$forceUpdate()
        this.selectedHorses.splice(index, 1)
      }
    },
    addAnotherResult() {
      this.selectedHorses.push({ horse: {}, eligible_from: this.getHolderDate() })
      /* Workaround for VueJS reactivity bug - scheduled to be fixed in VueJS 3. */
      this.$forceUpdate()
    },
    getHolderDate() {
      const d = this.$store.state.currentDate ? new Date(this.$store.state.currentDate) : new Date()
      // 0 indexed for Jan so add 1 to get current
      const day = d.getDate()
      const dayString = (`0${day}`).slice(-2)

      const month = d.getMonth() + 1
      const monthString = (`0${month}`).slice(-2)

      const year = d.getFullYear().toString()

      // Return the start of the ACA year
      return [year, monthString, dayString].join('-')
    }
  },
  mounted() {
    /* Get year data */
    const today = this.$store.state.currentDate ? new Date(this.$store.state.currentDate) : new Date()
    if (today.getMonth() < 3) {
      this.selectedYear = today.getFullYear() - 1
    } else {
      this.selectedYear = today.getFullYear()
    }
    const earliestYear = 2019
    for (let i = earliestYear; i < this.selectedYear; i++) {
      this.years.push({
        id: i,
        name: `${i} / ${i + 1}`
      })
    }

    this.years.push({
      id: this.selectedYear,
      name: `${this.selectedYear} / ${this.selectedYear + 1}`
    })
    this.years.push({
      id: this.selectedYear + 1,
      name: `${this.selectedYear + 1} / ${this.selectedYear + 2}`
    })

    /* Get rookie data */
    this.getRookies()
  }
}
</script>


// WEBPACK FOOTER //
// src/views/Titles/Edit/TitleEditDetailsRookie.vue